import { useApolloClient } from '@apollo/client'
import {
  AttendanceEmployeeFiltersInput,
  BulkUpdateAttendableAttendanceEmployeeDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'

interface BulkUpdateAttendableParams {
  employeeIds: string[]
  attendable: boolean
  startOn: Date
  endOn: Date
  selectAll?: boolean
  excludedEmployeeIds?: number[]
  filters?: AttendanceEmployeeFiltersInput | null
}

export const useBulkAttendableUpdate = () => {
  const client = useApolloClient()
  const [bulkUpdateAttendableMutation] = useMutation(
    BulkUpdateAttendableAttendanceEmployeeDocument,
    {
      extractorFn: (data) => data.attendanceMutations.bulkUpdateAttendableAttendanceEmployee,
    }
  )

  const bulkUpdateAttendable = async ({
    employeeIds,
    attendable,
    startOn,
    endOn,
    selectAll,
    excludedEmployeeIds,
    filters,
  }: BulkUpdateAttendableParams) => {
    const payload: {
      employeeIds: number[] | null
      attendable: boolean
      startOn: string
      endOn: string
      selectAll?: boolean
      excludedEmployeeIds?: number[]
      filters?: AttendanceEmployeeFiltersInput | null
    } = {
      employeeIds: employeeIds.map((id) => Number(id)),
      attendable,
      startOn: i18nDateToString(startOn, 'backend'),
      endOn: i18nDateToString(endOn, 'backend'),
    }

    if (selectAll) {
      payload.employeeIds = null
      payload.excludedEmployeeIds = excludedEmployeeIds
      payload.filters = filters || null
    }

    const result = await bulkUpdateAttendableMutation({ variables: payload })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      const trackingData = {
        employee_ids: employeeIds,
        employee_count: employeeIds.length,
      }

      if (attendable) {
        track(trackingEvents.timeTracking.bulkIncludeEmployees, trackingData)
      } else {
        track(trackingEvents.timeTracking.bulkExcludeEmployees, trackingData)
      }

      await client.refetchQueries({
        include: [
          'AttendanceCustomPeriodEmployeesCollectionList',
          'AttendanceCustomPeriodEmployeesAnalytics',
        ],
      })

      return {
        successful: employeeIds.length,
        failed: 0,
        errors: [],
      }
    }

    return {
      successful: 0,
      failed: employeeIds.length,
      errors: handled.success ? [] : handled.errors,
    }
  }

  return { bulkUpdateAttendable }
}
